import './SharedOpportunity.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlineMagnifyingGlass, HiOutlineMapPin, HiOutlineListBullet, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi2';
import { HiChevronRight, HiOutlineX } from 'react-icons/hi';
import { TbBuildingStore, TbFilter } from 'react-icons/tb';
import { BsCurrencyDollar } from 'react-icons/bs';
import { HiOutlineTrendingUp } from 'react-icons/hi';
import { HiOutlineShieldCheck } from 'react-icons/hi2';
import { HiOutlineSun } from 'react-icons/hi';
import { BsSnow } from 'react-icons/bs';
import Spinner from '../../components/Spinner/Spinner';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import BrandLogo from '../../components/BrandsTable/BrandLogo';
import { GoogleMapView, MapStore } from '../../components/GoogleMapView';
import HeatmapToggle from '../../components/HeatmapToggle/HeatmapToggle';
import StreetViewPanel from '../opportunityByRegion/StreetViewPanel';
import { calculateStoreRecommendations } from '../../utils/recommendationLogic';
import { sendVerificationCode, verifyCodeAndLogin } from '../../services/authService';
import { decodeJWTToken } from '../../services/graphqlClient';
import {
  fetchExportedOpportunity,
  ExportedOpportunity,
  MapConfig,
  DEFAULT_MAP_CONFIG,
} from '../../services/exportedOpportunitiesService';

type AccessState = 'loading' | 'open' | 'login' | 'otp' | 'granted' | 'denied' | 'error';

const SHARED_AUTH_KEY = 'shared_access_token';

function isEmailAllowed(email: string, allowedEmails: string[] | null, allowedDomains: string[] | null): boolean {
  const lower = email.toLowerCase();
  if (allowedEmails?.some(e => e.toLowerCase() === lower)) return true;
  const domain = lower.split('@')[1];
  if (domain && allowedDomains?.some(d => d.toLowerCase() === domain)) return true;
  return false;
}

function capitalizeWords(str: string): string {
  if (!str) return str;
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

function SharedOpportunity() {
  const { opportunityId } = useParams<{ opportunityId: string }>();

  // Access gate state
  const [accessState, setAccessState] = useState<AccessState>('loading');
  const [record, setRecord] = useState<ExportedOpportunity | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Main content state
  const [stores, setStores] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'map' | 'table'>('table');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [streetViewStore, setStreetViewStore] = useState<{ lat: number; lng: number; name?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapConfig, setMapConfig] = useState<MapConfig>(DEFAULT_MAP_CONFIG);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statesFilter, setStatesFilter] = useState<string[]>([]);
  const [citiesFilter, setCitiesFilter] = useState<string[]>([]);
  const [solutionsFilter, setSolutionsFilter] = useState<string[]>([]);
  const [crimeFilter, setCrimeFilter] = useState<string[]>([]);
  const [ordinanceFilter, setOrdinanceFilter] = useState('All');
  const [shoppingFilter, setShoppingFilter] = useState('All');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Check access on mount
  useEffect(() => {
    const checkAccess = async () => {
      if (!opportunityId) {
        setError('No opportunity ID provided');
        setAccessState('error');
        return;
      }

      // Try fetching with current auth (if any)
      let rec = await fetchExportedOpportunity(opportunityId);

      // If fetch failed, try with shared token or main app token
      if (!rec) {
        // Try with shared access token
        const sharedToken = localStorage.getItem(SHARED_AUTH_KEY);
        if (sharedToken) {
          localStorage.setItem('access_token', sharedToken);
          rec = await fetchExportedOpportunity(opportunityId);
          if (!rec) localStorage.removeItem(SHARED_AUTH_KEY);
        }
      }

      if (!rec) {
        // Try with main app token
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            const mainToken = parsed?.state?.token;
            if (mainToken) {
              localStorage.setItem('access_token', mainToken);
              rec = await fetchExportedOpportunity(opportunityId);
            }
          }
        } catch {
          // ignore parse errors
        }
      }

      // If still no record, show login form (not error) — user may need to authenticate
      if (!rec || !rec.poi_ids) {
        setAccessState('login');
        return;
      }

      setRecord(rec);
      const hasRestrictions = (rec.allowed_emails && rec.allowed_emails.length > 0) ||
        (rec.allowed_domains && rec.allowed_domains.length > 0);

      if (!hasRestrictions) {
        loadStores(rec);
        setAccessState('open');
        return;
      }

      // Check if user is already logged in as mod/bdo (main app token)
      try {
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          const parsed = JSON.parse(authStorage);
          const mainToken = parsed?.state?.token;
          if (mainToken) {
            const decoded = decodeJWTToken(mainToken);
            const role = decoded?.['https://hasura.io/jwt/claims']?.['x-hasura-default-role'];
            if (role === 'mod' || role === 'bdo') {
              loadStores(rec);
              setAccessState('granted');
              return;
            }
          }
        }
      } catch {
        // ignore parse errors
      }

      // Check if user already has a shared token
      const existingToken = localStorage.getItem(SHARED_AUTH_KEY);
      if (existingToken) {
        const decoded = decodeJWTToken(existingToken);
        if (decoded?.email && isEmailAllowed(decoded.email, rec.allowed_emails, rec.allowed_domains)) {
          loadStores(rec);
          setAccessState('granted');
          return;
        }
        localStorage.removeItem(SHARED_AUTH_KEY);
      }

      setAccessState('login');
    };

    checkAccess();
  }, [opportunityId]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const loadStores = (rec: ExportedOpportunity) => {
    setStores(Array.isArray(rec.poi_ids) ? rec.poi_ids : []);
    if (rec.map_config) setMapConfig(rec.map_config);
  };

  const handleSendOtp = async () => {
    if (!loginEmail.trim()) {
      setLoginError('Please enter your email address.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');

    // Only check email allowance if we already have the record (pre-authenticated fetch succeeded)
    if (record?.allowed_emails?.length || record?.allowed_domains?.length) {
      if (!isEmailAllowed(loginEmail.trim(), record.allowed_emails, record.allowed_domains)) {
        setLoginLoading(false);
        setLoginError('You do not have access to this opportunity.');
        return;
      }
    }

    const result = await sendVerificationCode(loginEmail.trim());
    setLoginLoading(false);

    if (result.success) {
      setAccessState('otp');
      setResendCooldown(120);
    } else {
      setLoginError(result.message);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.join('');
    if (code.length < 6) {
      setLoginError('Please enter the full 6-digit code.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');

    const result = await verifyCodeAndLogin(loginEmail.trim(), code);
    setLoginLoading(false);

    if (!result.success) {
      setLoginError(result.message || 'Verification failed.');
      return;
    }

    if (result.token) {
      localStorage.setItem(SHARED_AUTH_KEY, result.token);
      localStorage.setItem('access_token', result.token);
    }

    // If we don't have the record yet (couldn't fetch before login), fetch now
    let currentRecord = record;
    if (!currentRecord && opportunityId) {
      currentRecord = await fetchExportedOpportunity(opportunityId);
      if (currentRecord) setRecord(currentRecord);
    }

    if (!currentRecord || !currentRecord.poi_ids) {
      setError('Opportunity not found.');
      setAccessState('error');
      return;
    }

    const hasRestrictions = (currentRecord.allowed_emails && currentRecord.allowed_emails.length > 0) ||
      (currentRecord.allowed_domains && currentRecord.allowed_domains.length > 0);

    if (!hasRestrictions) {
      loadStores(currentRecord);
      setAccessState('granted');
      return;
    }

    const decoded = result.token ? decodeJWTToken(result.token) : null;
    if (decoded?.email && isEmailAllowed(decoded.email, currentRecord.allowed_emails, currentRecord.allowed_domains)) {
      loadStores(currentRecord);
      setAccessState('granted');
    } else {
      setAccessState('denied');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && otpCode.join('').length === 6) {
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otpCode];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || '';
    }
    setOtpCode(newOtp);
    const focusIdx = Math.min(pasted.length, 5);
    otpRefs.current[focusIdx]?.focus();
  };

  // --- Computed values for content ---
  const getStatus = (store: any) => store.customer_id ? 'Customer' : 'Non-Customer';

  const getSolutions = (store: any): string[] => {
    const arr: string[] = [];
    if (store.purchek_install_date) arr.push('Purchek');
    if (store.containment_install_date) arr.push('Cart Containment');
    if (store.cart_manager_install_date) arr.push('Cart Manager');
    if (store.subscription_start_date) arr.push('Subscription');
    if (store.paired_wheel_install_date) arr.push('Paired Wheel');
    return arr;
  };

  const getCrimeIndex = (crimeIncidents: number | undefined): string => {
    if (!crimeIncidents) return 'No Data';
    if (crimeIncidents >= 5000) return 'Critical';
    if (crimeIncidents >= 1000) return 'High';
    if (crimeIncidents >= 200) return 'Medium';
    if (crimeIncidents > 0) return 'Low';
    return 'No Data';
  };

  const getCrimeBadgeClass = (label: string): string => {
    switch (label) {
      case 'Critical': case 'High': return 'so-crime-badge high';
      case 'Medium': return 'so-crime-badge medium';
      case 'Low': return 'so-crime-badge low';
      default: return 'so-crime-badge';
    }
  };

  const brandName = useMemo(() => {
    if (stores.length === 0) return '';
    return stores[0]?.brand_name || '';
  }, [stores]);

  const totalSites = stores.length;

  const totalNeighbourhoodIncidents = useMemo(() => {
    return stores.reduce((sum: number, s: any) => sum + (s.geo?.total_crime_incidents || 0), 0);
  }, [stores]);

  const highCrimeSites = useMemo(() => {
    return stores.filter((s: any) => {
      const rate = (s.geo?.crime_rate || '').toLowerCase();
      return rate.startsWith('high') || rate.startsWith('critical');
    }).length;
  }, [stores]);

  const avgGkap = useMemo(() => {
    if (stores.length === 0) return '0%';
    const vals = stores.map((s: any) => s.nearby_penetration?.penetration || 0).filter(Boolean);
    if (vals.length === 0) return '0%';
    const avg = vals.reduce((a: number, b: number) => a + b, 0) / vals.length;
    return `${avg.toFixed(0)}%`;
  }, [stores]);

  // Compute recommendations per store (using default thresholds)
  const storeRecommendations = useMemo(() => {
    const map = new Map<string, ReturnType<typeof calculateStoreRecommendations>>();
    stores.forEach((s: any) => {
      const recs = calculateStoreRecommendations(
        s,
        (s as any).estimated_no_of_carts_per_store || 0,
      );
      map.set(s.id, recs);
    });
    return map;
  }, [stores]);

  const getStoreRecommendations = (storeId: string) => {
    return storeRecommendations.get(storeId) || {
      purchek: [], containment: [], cartManager: [], subscription: [], pairedWheel: [],
      purchekBadge: { matches: false, tooltip: '' },
      cartManagerBadge: { matches: false, tooltip: '' },
      containmentBadge: { matches: false, tooltip: '' },
      subscriptionBadge: { matches: false, tooltip: '' },
      pairedWheelBadge: { matches: false, tooltip: '' },
    };
  };

  const availableStates = useMemo(() => {
    const states = stores.map((s: any) => s.geo?.state ? capitalizeWords(s.geo.state) : '').filter(Boolean);
    return Array.from(new Set(states)).sort();
  }, [stores]);

  const availableCities = useMemo(() => {
    let filtered = stores;
    if (statesFilter.length > 0) {
      filtered = stores.filter((s: any) => statesFilter.includes(capitalizeWords(s.geo?.state || '')));
    }
    const cities = filtered.map((s: any) => s.geo?.city ? capitalizeWords(s.geo.city) : '').filter(Boolean);
    return Array.from(new Set(cities)).sort();
  }, [stores, statesFilter]);


  const solutionOptions = ['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel'];
  const crimeLevelOptions = ['High', 'Medium', 'Low', 'Critical', 'No Data'];

  const activeFilterCount =
    (typeFilter !== 'All Types' ? 1 : 0) +
    statesFilter.length +
    citiesFilter.length +
    solutionsFilter.length +
    crimeFilter.length +
    (ordinanceFilter !== 'All' ? 1 : 0) +
    (shoppingFilter !== 'All' ? 1 : 0);

  const handleClearAllFilters = () => {
    setTypeFilter('All Types');
    setStatesFilter([]);
    setCitiesFilter([]);
    setSolutionsFilter([]);
    setCrimeFilter([]);
    setOrdinanceFilter('All');
    setShoppingFilter('All');
  };

  const filteredStores = useMemo(() => {
    return stores.filter((s: any) => {
      const name = (s.site || s.brand_name || '').toLowerCase();
      const addr = (s.address || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || name.includes(q) || addr.includes(q);
      const matchState = statesFilter.length === 0 || statesFilter.includes(capitalizeWords(s.geo?.state || ''));
      const matchCity = citiesFilter.length === 0 || citiesFilter.includes(capitalizeWords(s.geo?.city || ''));
      const matchType = typeFilter === 'All Types' || getStatus(s) === typeFilter;
      const matchCrime = crimeFilter.length === 0 || crimeFilter.includes(getCrimeIndex(s.geo?.total_crime_incidents));
      const matchSolution = solutionsFilter.length === 0 || solutionsFilter.some(sol => getSolutions(s).includes(sol));
      const matchOrdinance = ordinanceFilter === 'All' ||
        (ordinanceFilter === 'Yes' && s.geo?.penalty_for_cart_owner) ||
        (ordinanceFilter === 'No' && !s.geo?.penalty_for_cart_owner);
      const plazaCmValue = s.nearby_penetration?.plaza_cm_customer_count ?? 0;
      const isInComplex = plazaCmValue !== 0 && plazaCmValue !== null;
      const matchShopping = shoppingFilter === 'All' ||
        (shoppingFilter === 'Yes, with containment store(s) in the complex' && isInComplex) ||
        (shoppingFilter === 'Yes, without containment store(s) in the complex' && !isInComplex);

      return matchSearch && matchState && matchCity && matchType && matchCrime && matchSolution && matchOrdinance && matchShopping;
    });
  }, [stores, searchQuery, statesFilter, citiesFilter, typeFilter, crimeFilter, solutionsFilter, ordinanceFilter, shoppingFilter]);

  const mapStores: MapStore[] = useMemo(() => {
    return filteredStores
      .filter((s: any) => s.latitude && s.longitude)
      .map((s: any) => ({
        id: s.id || String(s.id),
        brand_name: s.brand_name ?? undefined,
        site: s.site ?? undefined,
        latitude: s.latitude,
        longitude: s.longitude,
        customer_id: s.customer_id ?? undefined,
        channel: s.channel ?? undefined,
        address: s.address ?? undefined,
        city: s.geo?.city ?? undefined,
        state: s.geo?.state ?? undefined,
        zipcode: s.zipcode || s.geo?.zipcode || undefined,
        purchek_install_date: s.purchek_install_date ?? undefined,
        containment_install_date: s.containment_install_date ?? undefined,
        cart_manager_install_date: s.cart_manager_install_date ?? undefined,
        subscription_start_date: s.subscription_start_date ?? undefined,
        paired_wheel_install_date: s.paired_wheel_install_date ?? undefined,
        estimated_no_of_carts_per_store: s.estimated_no_of_carts_per_store ?? undefined,
        geo: {
          city: s.geo?.city ?? undefined,
          state_code: s.geo?.state ?? undefined,
          total_crime_incidents: s.geo?.total_crime_incidents ?? 0,
        },
        nearby_penetration: {
          plaza_cm_customer_count: s.plaza_cm_customer_count ?? 0,
        },
        recommendations: s.recommendations,
        total_crime_amount_usd: s.total_crime_amount_usd ?? null,
        video_incident_count: s.video_incident_count ?? null,
        is_customer: !!s.customer_id,
      }));
  }, [filteredStores]);

  const fc = mapConfig.include_filters;

  // --- Loading ---
  if (accessState === 'loading') {
    return (
      <div className="so-page">
        <div className="so-loader">
          <Spinner size="large" />
          <p className="so-loader-text">Loading shared opportunity...</p>
        </div>
      </div>
    );
  }

  // --- Error ---
  if (accessState === 'error') {
    return (
      <div className="so-page">
        <div className="so-access-gate">
          <div className="so-access-card">
            <h2>Opportunity Not Found</h2>
            <p className="so-access-subtitle">{error || 'This opportunity does not exist or has been removed.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Access Denied ---
  if (accessState === 'denied') {
    return (
      <div className="so-page">
        <div className="so-access-gate">
          <div className="so-access-card">
            <div className="so-access-icon denied">
              <HiOutlineShieldCheck />
            </div>
            <h2>Access Denied</h2>
            <p className="so-access-subtitle">Your email does not have permission to view this opportunity.</p>
            <button className="so-access-btn secondary" onClick={() => {
              localStorage.removeItem(SHARED_AUTH_KEY);
              setLoginEmail('');
              setOtpCode(['', '', '', '', '', '']);
              setLoginError('');
              setAccessState('login');
            }}>
              Try a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Login form ---
  if (accessState === 'login') {
    return (
      <div className="so-page">
        <div className="so-access-gate">
          <div className="so-access-card">
            <div className="so-access-icon">
              <HiOutlineShieldCheck />
            </div>
            <h2>Verify Your Identity</h2>
            <p className="so-access-subtitle">Enter your email to access this shared opportunity.</p>
            <div className="so-login-form">
              <input
                type="email"
                className="so-login-input"
                placeholder="Enter your email address"
                value={loginEmail}
                onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                disabled={loginLoading}
              />
              {loginError && <p className="so-login-error">{loginError}</p>}
              <button className="so-access-btn" onClick={handleSendOtp} disabled={loginLoading}>
                {loginLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- OTP verification ---
  if (accessState === 'otp') {
    return (
      <div className="so-page">
        <div className="so-access-gate">
          <div className="so-access-card">
            <div className="so-access-icon">
              <HiOutlineShieldCheck />
            </div>
            <h2>Enter Verification Code</h2>
            <p className="so-access-subtitle">
              A 6-digit code has been sent to <strong>{loginEmail}</strong>
            </p>
            <div className="so-otp-inputs" onPaste={handleOtpPaste}>
              {otpCode.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="so-otp-box"
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  disabled={loginLoading}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {loginError && <p className="so-login-error">{loginError}</p>}
            <button className="so-access-btn" onClick={handleVerifyOtp} disabled={loginLoading || otpCode.join('').length < 6}>
              {loginLoading ? 'Verifying...' : 'Verify & Continue'}
            </button>
            <div className="so-otp-actions">
              <button
                className="so-link-btn"
                onClick={() => { setAccessState('login'); setOtpCode(['', '', '', '', '', '']); setLoginError(''); }}
              >
                Change email
              </button>
              <button
                className="so-link-btn"
                onClick={handleSendOtp}
                disabled={resendCooldown > 0 || loginLoading}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Main content (open or granted) ---
  return (
    <div className="so-page">
      <div className="so-header">
        <div className="so-header-left">
          {brandName && <BrandLogo brandName={brandName} />}
          <div className="so-title">
            <h1>{brandName || 'Shared Opportunity'}</h1>
            <p className="so-subtitle">{stores.length} stores</p>
          </div>
        </div>
        <div className="so-header-right">
          <div className="so-view-toggle">
            <button className={`so-view-btn ${viewMode === 'map' ? 'active' : ''}`} onClick={() => setViewMode('map')}>
              <HiOutlineMapPin /> Map View
            </button>
            <button className={`so-view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
              <HiOutlineListBullet /> Table View
            </button>
          </div>
        </div>
      </div>

      {mapConfig.include_kpis.enabled && (
        <div className="so-kpi-row">
          {mapConfig.include_kpis.total_sites && (
            <div className="so-kpi-card green">
              <div className="so-kpi-icon"><TbBuildingStore /></div>
              <div className="so-kpi-info">
                <span className="so-kpi-label">Total Sites</span>
                <span className="so-kpi-value">{totalSites}</span>
              </div>
            </div>
          )}
          {mapConfig.include_kpis.total_neighbourhood_incidents && (
            <div className="so-kpi-card green">
              <div className="so-kpi-icon"><BsCurrencyDollar /></div>
              <div className="so-kpi-info">
                <span className="so-kpi-label">Total Neighbourhood Incidents</span>
                <span className="so-kpi-value">{totalNeighbourhoodIncidents.toLocaleString()}</span>
              </div>
            </div>
          )}
          {mapConfig.include_kpis.sites_in_high_crime_area && (
            <div className="so-kpi-card teal">
              <div className="so-kpi-icon"><HiOutlineTrendingUp /></div>
              <div className="so-kpi-info">
                <span className="so-kpi-label">Sites in High Crime Area</span>
                <span className="so-kpi-value">{highCrimeSites}</span>
              </div>
            </div>
          )}
          <div className="so-kpi-card orange">
            <div className="so-kpi-icon"><HiOutlineShieldCheck /></div>
            <div className="so-kpi-info">
              <span className="so-kpi-label">Avg GKAP</span>
              <span className="so-kpi-value">{avgGkap}</span>
            </div>
          </div>
        </div>
      )}

      {fc.enabled && (
        <div className="so-filters-tab">
          <div className="so-filters-tab-header" onClick={() => setFiltersOpen(!filtersOpen)} style={{ cursor: 'pointer' }}>
            <TbFilter className="so-filters-tab-icon" />
            <span className="so-filters-tab-title">Filters</span>
            {activeFilterCount > 0 && <span className="so-filter-badge">{activeFilterCount}</span>}
            <span className="so-filters-toggle">
              {filtersOpen ? <HiOutlineChevronUp size={16} /> : <HiOutlineChevronDown size={16} />}
            </span>
          </div>

          {/* Search always visible */}
          <div className="so-filters-search-row">
            <div className="so-search-box">
              <HiOutlineMagnifyingGlass className="so-search-icon" />
              <input
                type="text"
                className="so-search-input"
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <span className="so-stores-count">{filteredStores.length} of {stores.length} stores</span>
          </div>

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div className="so-active-filters">
              {typeFilter !== 'All Types' && (
                <span className="so-filter-chip">{typeFilter} <button onClick={() => setTypeFilter('All Types')}><HiOutlineX size={12} /></button></span>
              )}
              {statesFilter.map(st => (
                <span key={st} className="so-filter-chip">{st} <button onClick={() => setStatesFilter(statesFilter.filter(s => s !== st))}><HiOutlineX size={12} /></button></span>
              ))}
              {citiesFilter.map(c => (
                <span key={c} className="so-filter-chip">{c} <button onClick={() => setCitiesFilter(citiesFilter.filter(x => x !== c))}><HiOutlineX size={12} /></button></span>
              ))}
              {solutionsFilter.map(s => (
                <span key={s} className="so-filter-chip">{s} <button onClick={() => setSolutionsFilter(solutionsFilter.filter(x => x !== s))}><HiOutlineX size={12} /></button></span>
              ))}
              {crimeFilter.map(c => (
                <span key={c} className="so-filter-chip">Crime: {c} <button onClick={() => setCrimeFilter(crimeFilter.filter(x => x !== c))}><HiOutlineX size={12} /></button></span>
              ))}
              {ordinanceFilter !== 'All' && (
                <span className="so-filter-chip">Ordinance: {ordinanceFilter} <button onClick={() => setOrdinanceFilter('All')}><HiOutlineX size={12} /></button></span>
              )}
              {shoppingFilter !== 'All' && (
                <span className="so-filter-chip">{shoppingFilter === 'Yes, with containment store(s) in the complex' ? 'Shopping Complex: With CM' : 'Shopping Complex: Without CM'} <button onClick={() => setShoppingFilter('All')}><HiOutlineX size={12} /></button></span>
              )}
              <button className="so-clear-all-btn" onClick={handleClearAllFilters}>Clear All</button>
            </div>
          )}

          {/* Collapsible filter body */}
          {filtersOpen && (
            <div className="so-filters-body">
              <div className="so-filters-grid">
                {fc.customer_type && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">Customer Status</label>
                    <CustomDropdown value={typeFilter} options={['All Types', 'Customer', 'Non-Customer']} onChange={setTypeFilter} />
                  </div>
                )}
                {fc.state && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">State</label>
                    <MultiSelectDropdown values={statesFilter} options={availableStates} onChange={setStatesFilter} placeholder="All states" />
                  </div>
                )}
                {fc.city && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">City</label>
                    <MultiSelectDropdown values={citiesFilter} options={availableCities} onChange={setCitiesFilter} placeholder="All cities" />
                  </div>
                )}
                {fc.installed_solution && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">Installed Solutions</label>
                    <MultiSelectDropdown values={solutionsFilter} options={solutionOptions} onChange={setSolutionsFilter} placeholder="All solutions" />
                  </div>
                )}
                {fc.crime_index && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">Crime Level</label>
                    <MultiSelectDropdown values={crimeFilter} options={crimeLevelOptions} onChange={setCrimeFilter} placeholder="All crime levels" />
                  </div>
                )}
                {fc.cart_ordinance && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">Cart Ordinance</label>
                    <CustomDropdown value={ordinanceFilter} options={['All', 'Yes', 'No']} onChange={setOrdinanceFilter} />
                  </div>
                )}
                {fc.shopping_complex && (
                  <div className="so-filter-group">
                    <label className="so-filter-label">Located in a Shopping Complex</label>
                    <CustomDropdown value={shoppingFilter} options={['All', 'Yes, with containment store(s) in the complex', 'Yes, without containment store(s) in the complex']} onChange={setShoppingFilter} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="so-content">
        {error ? (
          <div className="so-error">{error}</div>
        ) : (
          <>
          <div className="so-map-container" style={{ display: viewMode === 'map' ? 'block' : 'none' }}>
            <div className="allsites-map-inline" style={{ position: 'relative' }}>
              <GoogleMapView
                stores={mapStores}
                fitBounds={true}
                showHeatmap={showHeatmap}
                showDetailedMarkers={true}
                onStreetViewClick={(position, name) => setStreetViewStore({ lat: position.lat, lng: position.lng, name })}
                infoWindowOptions={{ showNeighbourhoodButton: false }}
                height="100%"
              />
              <HeatmapToggle showHeatmap={showHeatmap} onToggle={setShowHeatmap} />
              <div className="map-legend">
                <span className="map-legend-item"><span className="map-legend-dot blue"></span>Customer</span>
                <span className="map-legend-item"><span className="map-legend-dot red"></span>Non-Customer</span>
                <span className="map-legend-divider"></span>
                <span className="map-legend-item"><span className="map-legend-badge">P</span>Purchek</span>
                <span className="map-legend-item"><span className="map-legend-badge">CC</span>Cart Containment</span>
                <span className="map-legend-item"><span className="map-legend-badge">CM</span>Cart Manager</span>
                <span className="map-legend-item"><span className="map-legend-badge">S</span>Subscription</span>
              </div>
            </div>
            {streetViewStore && (
              <StreetViewPanel
                lat={streetViewStore.lat}
                lng={streetViewStore.lng}
                storeName={streetViewStore.name}
                onClose={() => setStreetViewStore(null)}
              />
            )}
          </div>
          {viewMode === 'table' && (
          <div className="so-stores-table-wrapper">
            {filteredStores.length === 0 ? (
              <div className="so-empty">No stores match the selected filters</div>
            ) : (
              <table className="so-stores-table">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th>#</th>
                    <th>Store</th>
                    <th>Brand</th>
                    {fc.customer_type && <th>Status</th>}
                    {fc.installed_solution && <th>Installed Solutions</th>}
                    {fc.recommended_solution && <th>Recommended</th>}
                    {fc.crime_index && <th>Crime Index</th>}
                    {fc.cart_ordinance && <th>Cart Ordinance</th>}
                    {fc.weather_condition && <th>Weather</th>}
                    {fc.shopping_complex && <th>Shopping Complex</th>}
                    <th>GKAP %</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStores.map((store: any, idx: number) => {
                    const solutions = getSolutions(store);
                    const crimeLabel = getCrimeIndex(store.geo?.total_crime_incidents);
                    const hotDays = store.geo?.no_of_hot_days ?? 0;
                    const coldDays = store.geo?.no_of_cold_days ?? 0;
                    const recs = getStoreRecommendations(store.id);
                    const hasRecs = recs.purchek.length > 0 || recs.containment.length > 0 || recs.cartManager.length > 0 || recs.subscription.length > 0 || recs.pairedWheel.length > 0;
                    const recList: { type: string; label: string }[] = [];
                    if (recs.purchek.length > 0) recList.push({ type: 'purchek', label: 'Purchek' });
                    if (recs.containment.length > 0) recList.push({ type: 'containment', label: 'Containment' });
                    if (recs.cartManager.length > 0) recList.push({ type: 'cartManager', label: 'Cart Manager' });
                    if (recs.subscription.length > 0) recList.push({ type: 'subscription', label: 'Subscription' });
                    if (recs.pairedWheel.length > 0) recList.push({ type: 'pairedWheel', label: 'Paired Wheel' });
                    return (
                    <React.Fragment key={store.id || idx}>
                    <tr className={expandedRow === store.id ? 'so-row-expanded' : ''} onClick={() => setExpandedRow(expandedRow === store.id ? null : store.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <button className="so-expand-btn" onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === store.id ? null : store.id); }}>
                          <HiChevronRight className={`so-expand-icon ${expandedRow === store.id ? 'expanded' : ''}`} />
                        </button>
                      </td>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="so-store-cell">
                          <div className="so-store-name">{store.brand_name || store.site || '-'}</div>
                          <div className="so-store-id">{store.id || ''}</div>
                          <div className="so-store-address">{store.address || '-'}</div>
                          <div className="so-store-location">
                            {store.geo?.city ? capitalizeWords(store.geo.city) : ''}{store.geo?.city && store.geo?.state ? ', ' : ''}{store.geo?.state ? capitalizeWords(store.geo.state) : ''}
                            {store.zipcode ? ` ${store.zipcode}` : ''}
                          </div>
                        </div>
                      </td>
                      <td>{store.brand_name || '-'}</td>
                      {fc.customer_type && (
                        <td>
                          <span className={`so-status-badge ${getStatus(store) === 'Customer' ? 'customer' : 'non-customer'}`}>
                            {getStatus(store)}
                          </span>
                        </td>
                      )}
                      {fc.installed_solution && (
                        <td>
                          <div className="so-solutions-container">
                            {solutions.length > 0 ? solutions.map((s, i) => (
                              <span key={i} className="so-solution-badge installed">{s}</span>
                            )) : <span className="so-solution-none">None</span>}
                          </div>
                        </td>
                      )}
                      {fc.recommended_solution && (
                        <td>
                          {hasRecs ? (
                            <div className="so-solutions-container">
                              {recList.map((rec, i) => (
                                <span key={i} className={`so-recommendation-badge ${rec.type}`}>{rec.label}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="so-solution-none">None</span>
                          )}
                        </td>
                      )}
                      {fc.crime_index && (
                        <td>
                          <span className={getCrimeBadgeClass(crimeLabel)}>{crimeLabel}</span>
                        </td>
                      )}
                      {fc.cart_ordinance && (
                        <td>
                          <span className={`so-penalty-badge ${store.geo?.penalty_for_cart_owner ? 'yes' : 'no'}`}>
                            {store.geo?.penalty_for_cart_owner ? 'Yes' : 'No'}
                          </span>
                        </td>
                      )}
                      {fc.weather_condition && (
                        <td>
                          <div className="so-weather-cell">
                            {hotDays > 0 && (
                              <span className="so-weather-item hot">
                                <HiOutlineSun size={14} />
                                {hotDays}
                              </span>
                            )}
                            {coldDays > 0 && (
                              <span className="so-weather-item cold">
                                <BsSnow size={12} />
                                {coldDays}
                              </span>
                            )}
                            {!hotDays && !coldDays && <span className="so-weather-item none">-</span>}
                          </div>
                        </td>
                      )}
                      {fc.shopping_complex && <td>{(store.nearby_penetration?.plaza_cm_customer_count ?? 0) > 0 ? 'Yes' : 'No'}</td>}
                      <td>{store.nearby_penetration?.penetration != null ? `${store.nearby_penetration.penetration.toFixed(2)}%` : '-'}</td>
                    </tr>
                    {expandedRow === store.id && (
                      <tr className="so-expanded-content">
                        <td colSpan={20}>
                          <div className="so-expanded-details">
                            {hasRecs ? (
                              <div className="so-recommendations-section">
                                <div className="so-recommendations-list">
                                  {recs.purchek.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Purchek Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.purchek.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.containment.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Cart Containment Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.containment.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.cartManager.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Cart Manager Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.cartManager.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.subscription.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Subscription Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.subscription.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.pairedWheel.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Paired Wheel Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.pairedWheel.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="so-no-recommendations">
                                <p>No recommended solution based on current conditions. The store may still be at risk and need protection.</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

export default SharedOpportunity;
